import { SingletonInterface, DILList, DIL, DILProperty, UserDetails } from './types';

/**
 * hostname: hagar.pnnl.gov
 * path: /srs/datachannels/DevVPS_v4/
 * url: hagar.pnnl.gov/srs/datachannels/DevVPS_v4/
 */

class singleton implements SingletonInterface {
  hostName: string | null = null;
  path: string | null = null;
  DIL: DILList | null = null;
  userDetails: UserDetails | null = null;
  unauthenticatedCallback?(): void;

  setHostName(hostName: string) {
    this.hostName = hostName;
  }

  setPath(path: string) {
    this.path = path;
  }

  setDil(DIL: DILList) {
    this.DIL = DIL;
  }

  setUserDetails(userDetails: UserDetails) {
    this.userDetails = userDetails;
  }

  setUnauthenticatedCallback(callback?: () => void) {
    this.unauthenticatedCallback = callback;
  }

  getHostName() {
    return this.hostName;
  }

  getPath() {
    return this.path;
  }

  getUrl() {
    return `https://${this.hostName}${this.path}`;
  }

  getDIL() {
    return this.DIL;
  }

  getUserName(): string | null {
    return this.userDetails?.userId ?? null;
  }
}

const instance = new singleton();

export const selectDIL = () => instance.getDIL();
export const selectUserName = () => instance.getUserName();

/**
 * Return a DIL object given the DIL name
 * @param name
 */
export function selectDilDefinitionByName(name: string): DIL | null {
  const DIL = instance.getDIL();
  let result = null;
  if (DIL) {
    result = Object.values(DIL)?.find(property => property.name === name) ?? null;
  }

  // warn while not in production upon bad stuff
  if (process.env.NODE_ENV !== 'production') {
    if (!result && DIL) {
      console.warn(`Property "${name}" not found on DIL. See below for available properties.`);
      console.warn(`${Object.values(DIL).map(property => ` ${property.name}`)}`);
    } else if (!DIL) {
      console.warn(`Selection of a DIL definition was attempted before the DIL was defined`);
    }
  }
  return result;
}

/**
 * Get an array of DILProperty objects given a DIL and an array of property names.
 * @param DIL
 * @param names
 */
function selectPropertyListFromDilDefinitionByNames(DIL: DIL | null, names: string[]): DILProperty[] {
  let result: DILProperty[] = [];
  if (DIL) {
    // Properties are grabbed from the DIL in a sorted order rather than with a filter because
    // endpoints like "Property Hierarchy" work differently based on the array order of DIL properties.
    const propertyList = Object.values(DIL.propertyList);
    const subResult = names.map(name => propertyList.find(property => property.name === name));
    result = subResult.filter(result => result != null) as DILProperty[];
  }

  // warn while not in production upon bad stuff
  if (process.env.NODE_ENV !== 'production') {
    if (names.length > result.length) {
      console.warn(`A name provided did not exist in the DIL`);
      console.warn(`Names selected: ${names.sort()}`);
      console.warn(`Names found:    ${result.map(({ name }) => name).sort()}`);
      if (DIL) {
        console.warn(`Names available: ${Object.values(DIL.propertyList).map(dilProperty => ` ${dilProperty.name}`)}`);
      }
    }
  }
  return result;
}

export function selectPropertyListFromDilByNames(name: string, names: string[]): DILProperty[] {
  const dilDefinition: DIL | null = selectDilDefinitionByName(name);
  return selectPropertyListFromDilDefinitionByNames(dilDefinition, names);
}

export default instance;
